import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Button,
  Table,
  Tag,
  Popover,
  DatePicker,
  Space,
  message,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import {
  getBankStatements,
  getBankStatementFilterValues,
  exportBankStatements,
} from "../api/accounting";
import DescriptionFilterDropdown from "./Shared/Utils/DescriptionFilterDropdown";
import ExcelExportModal from "./Shared/Modals/ExcelExportModal";
import dayjs from "dayjs";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Shared/Utils/BankStatements.css"; // include custom CSS for backgrounds
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
const BankStatements = forwardRef(
  ({ filters, selectedBrand, onAffectPayment, onAffectOverpayment }, ref) => {
    const brandId = selectedBrand?.id;
    const startMs = filters?.dateRange?.[0]?.valueOf();
    const endMs = filters?.dateRange?.[1]?.valueOf();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const ran = useRef(false);
    const {RangePicker} = DatePicker;
    const FILTER_KEY = "bankStatementsFilters";
    // Persisted pagination key
    const PAGINATION_KEY = "bankStatementsPagination";
    // Load persisted pagination from localStorage
    const getInitialPagination = () => {
      try {
        const saved = localStorage.getItem(PAGINATION_KEY);
        return saved ? JSON.parse(saved) : { currentPage: 1, pageSize: 100 };
      } catch {
        return { currentPage: 1, pageSize: 100 };
      }
    };
    const initialPagination = getInitialPagination();
    const [currentPage, setCurrentPage] = useState(
      initialPagination.currentPage
    );
    const [pageSize, setPageSize] = useState(initialPagination.pageSize);
    const [totalItems, setTotalItems] = useState(0);

    // Sorting states
    // Persistent filters and sort state
    const getInitialPersistedState = () => {
      try {
        const saved = localStorage.getItem(FILTER_KEY);
        return saved
          ? JSON.parse(saved)
          : { filters: {}, sortField: null, sortOrder: null };
      } catch {
        return { filters: {}, sortField: null, sortOrder: null };
      }
    };
    const initialPersisted = getInitialPersistedState();
    const [tableFilters, setTableFilters] = useState(
      initialPersisted.filters || {}
    );
    const [sortField, setSortField] = useState(
      initialPersisted.sortField || null
    );
    const [sortOrder, setSortOrder] = useState(
      initialPersisted.sortOrder || null
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (ran.current) {
        handleTableChange(
          { current: 1, pageSize },
          { ...tableFilters, ...filters },
          { field: sortField, order: sortOrder }
        );
      }
    }, [filters]);

    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterValues, setFilterValues] = useState({
      bank: [],
      description: [],
      detail: [],
      type: [],
      vendor: [],
    });

    // Excel export function exposed via ref
    // Updated exportToExcel function in BankStatements.jsx
    const exportToExcel = async (returnFile = false) => {
      try {
        setExportLoading(true);

        // Prepare filters object to send to backend
        const exportFilters = {
          // Brand and date filters
          brand: selectedBrand ? selectedBrand.id : null,
          start_date:
            filters.dateRange &&
            filters.dateRange.length === 2 &&
            filters.dateRange[0]
              ? filters.dateRange[0].format("YYYY-MM-DD")
              : null,
          end_date:
            filters.dateRange &&
            filters.dateRange.length === 2 &&
            filters.dateRange[1]
              ? filters.dateRange[1].format("YYYY-MM-DD")
              : null,

          // Include allocations
          include_allocations: true,

          // Table filters - convert to arrays for backend processing
          bank:
            tableFilters.bank && tableFilters.bank.length > 0
              ? tableFilters.bank
              : null,
          description:
            tableFilters.description && tableFilters.description.length > 0
              ? tableFilters.description
              : null,
          detail:
            tableFilters.detail && tableFilters.detail.length > 0
              ? tableFilters.detail
              : null,
          type:
            tableFilters.type && tableFilters.type.length > 0
              ? tableFilters.type
              : null,
          vendor:
            tableFilters.vendor && tableFilters.vendor.length > 0
              ? tableFilters.vendor
              : null,
          allocationStatus:
            tableFilters.allocationStatus &&
            tableFilters.allocationStatus.length > 0
              ? tableFilters.allocationStatus
              : null,

          // Export format
          format: "xlsx",
        };

        console.log(
          "Exporting bank statements with backend filters:",
          exportFilters
        );

        if (returnFile) {
          // For email functionality, we need to get the file as blob and convert to File object
          try {
            const response = await exportBankStatements(exportFilters);

            // Create a File object from the blob response
            const blob = response.data;
            const timestamp = new Date()
              .toISOString()
              .slice(0, 19)
              .replace(/:/g, "-");
            const filename = `bank_statements_export_${timestamp}.xlsx`;

            return new File([blob], filename, {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
          } catch (error) {
            console.error("Error creating file for email:", error);
            throw new Error(
              `Failed to generate file for email: ${error.message}`
            );
          }
        } else {
          // For direct download
          try {
            const response = await exportBankStatements(exportFilters);

            // Validate response
            if (!response || !response.data) {
              throw new Error("Invalid response from server");
            }

            // Create blob URL and trigger download
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Try to get filename from response headers
            const contentDisposition = response.headers["content-disposition"];
            let filename = "bank_statements_export.xlsx";

            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(
                /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
              );
              if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, "");
              }
            } else {
              // Generate filename with timestamp if not provided
              const timestamp = new Date()
                .toISOString()
                .slice(0, 19)
                .replace(/:/g, "-");
              filename = `bank_statements_export_${timestamp}.xlsx`;
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success("Export completed successfully!");
          } catch (error) {
            console.error("Error during direct download:", error);
            throw new Error(`Download failed: ${error.message}`);
          }
        }
      } catch (error) {
        console.error("Error exporting to Excel:", error);

        // More specific error messages
        let errorMessage = "Failed to export to Excel";

        if (error.response) {
          switch (error.response.status) {
            case 405:
              errorMessage =
                "Export method not allowed. Please check the server configuration.";
              break;
            case 404:
              errorMessage =
                "Export endpoint not found. Please check the URL configuration.";
              break;
            case 500:
              errorMessage =
                "Server error during export. Please try again or contact support.";
              break;
            case 403:
              errorMessage = "You don't have permission to export data.";
              break;
            case 401:
              errorMessage = "Authentication failed. Please log in again.";
              break;
            default:
              errorMessage = `Export failed with status ${
                error.response.status
              }: ${error.response.data?.error || error.message}`;
          }
        } else if (error.request) {
          errorMessage =
            "Network error. Please check your internet connection.";
        } else {
          errorMessage =
            error.message || "Unknown error occurred during export";
        }

        message.error(errorMessage);
        throw error;
      } finally {
        setExportLoading(false);
      }
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      exportToExcel,
    }));

    const fetchFilterValues = useCallback(async () => {
      if (!brandId) return;
      try {
        const data = await getBankStatementFilterValues(brandId);
        setFilterValues(data);
      } catch (error) {
        console.error("Error fetching filter values:", error);
        message.error("Failed to load filter options");
      }
    }, [brandId]);

    useEffect(() => {
      fetchFilterValues();
    }, [fetchFilterValues]);

    const fetchTransactions = useCallback(
      async (
        page,
        size,
        currentFilters,
        currentSortField,
        currentSortOrder
      ) => {
        const pageToFetch = page || currentPage;
        const sizeToFetch = size || pageSize;
        const filtersToUse = currentFilters || tableFilters;
        const sortFieldToUse = currentSortField || sortField;
        const sortOrderToUse = currentSortOrder || sortOrder;

        if (!brandId || !startMs || !endMs) return;

        try {

          // Map merged filters to API params
          const apiParams = {
            brand: selectedBrand.id,
            include_allocations: true,
            page: pageToFetch,
            page_size: sizeToFetch,
            sort_field: sortFieldToUse,
            sort_order: sortOrderToUse,
            // Date range from filters prop (global)
            ...(filtersToUse.dateRange &&
              filtersToUse.dateRange.length === 2 && {
                start_date: filtersToUse.dateRange[0]?.format
                  ? filtersToUse.dateRange[0].format("YYYY-MM-DD")
                  : typeof filtersToUse.dateRange[0] === "string"
                  ? filtersToUse.dateRange[0].slice(0, 10)
                  : filtersToUse.dateRange[0],
                end_date: filtersToUse.dateRange[1]?.format
                  ? filtersToUse.dateRange[1].format("YYYY-MM-DD")
                  : typeof filtersToUse.dateRange[1] === "string"
                  ? filtersToUse.dateRange[1].slice(0, 10)
                  : filtersToUse.dateRange[1],
              }),
            // Partner type from filters prop (global)
            ...(filtersToUse.partnerType &&
              filtersToUse.partnerType !== "all" && {
                partner_type: filtersToUse.partnerType,
              }),
            // Status from filters prop (global)
            ...(filtersToUse.status &&
              filtersToUse.status !== "all" && { status: filtersToUse.status }),
            // Table filters (column)
            ...(filtersToUse.bank && {
              bank: Array.isArray(filtersToUse.bank)
                ? filtersToUse.bank.join(",")
                : filtersToUse.bank,
            }),
            ...(filtersToUse.description && {
              description: Array.isArray(filtersToUse.description)
                ? filtersToUse.description.join(",")
                : filtersToUse.description,
            }),
            ...(filtersToUse.detail && {
              detail: Array.isArray(filtersToUse.detail)
                ? filtersToUse.detail.join(",")
                : filtersToUse.detail,
            }),
            ...(filtersToUse.type && {
              type: Array.isArray(filtersToUse.type)
                ? filtersToUse.type.join(",")
                : filtersToUse.type,
            }),
            ...(filtersToUse.vendor && {
              vendor: Array.isArray(filtersToUse.vendor)
                ? filtersToUse.vendor.join(",")
                : filtersToUse.vendor,
            }),
            ...(filtersToUse.allocationStatus && {
              allocation_status: Array.isArray(filtersToUse.allocationStatus)
                ? filtersToUse.allocationStatus.join(",")
                : filtersToUse.allocationStatus,
            }),
          };
          console.log("BankStatements API params:", apiParams);

          const data = await getBankStatements(apiParams);
          console.log("Fetched bank statements:", data);

          setTotalItems(data.total);

          const sorted = data.results.sort(
            (a, b) =>
              new Date(a.transaction_date) - new Date(b.transaction_date)
          );

          let balance = 0;
          const processed = sorted.map((tx) => {
            if (sorted.indexOf(tx) < 3) {
            console.log('Transaction data for statements:', {
              id: tx.id,
              tx_notes: tx.notes,
              statement_notes: tx.statement__notes,
              statement_id: tx.statement_id,
              all_keys: Object.keys(tx)
            });
          }
            const amt = parseFloat(tx.amount);
            let debit = "-",
              credit = "-",
              effect = 0;

            if (
              ["deposit", "refund", "interest"].includes(tx.transaction_type)
            ) {
              credit = amt.toFixed(2);
              effect = amt;
            } else if (
              ["withdrawal", "payment", "fee"].includes(tx.transaction_type)
            ) {
              debit = Math.abs(amt).toFixed(2);
              effect = -Math.abs(amt);
            } else if (tx.transaction_type === "transfer") {
              if (amt >= 0) {
                credit = amt.toFixed(2);
                effect = amt;
              } else {
                debit = Math.abs(amt).toFixed(2);
                effect = amt;
              }
            } else {
              if (amt >= 0) {
                credit = amt.toFixed(2);
                effect = amt;
              } else {
                debit = Math.abs(amt).toFixed(2);
                effect = amt;
              }
            }
            balance += effect;

            const allocs = tx.payment_allocations || [];
            let allocStatus, pct;

            if (tx.allocation_status && tx.percent_allocated !== undefined) {
              allocStatus = tx.allocation_status;
              pct = tx.percent_allocated;
            } else {
              const totalAlloc =
                tx.allocated_amount ||
                allocs.reduce((s, a) => s + parseFloat(a.amount || 0), 0);
              pct = (totalAlloc / Math.abs(amt)) * 100;
              allocStatus =
                pct >= 99.99
                  ? { type: "fully_allocated", label: "Fully Allocated" }
                  : pct > 0
                  ? {
                      type: "partially_allocated",
                      label: "Partially Allocated",
                    }
                  : { type: "unallocated", label: "Unallocated" };
            }

            return {
              ...tx,
              debit,
              credit,
              balance,
              allocations: allocs,
              allocationStatus: allocStatus,
              allocatedAmount: tx.allocated_amount || 0,
              percentAllocated: pct,
              notes : tx.statement__notes || tx.notes || null
            };
          
          });
          console.log("Processed transactions:", processed);
          setTransactions(processed);
        } catch (err) {
          console.error(err);
          message.error("Failed to load transactions");
        } finally {
          setLoading(false);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [brandId, startMs, endMs, selectedBrand, filters, pageSize]
    );

    useEffect(() => {
      if (ran.current) return;
      ran.current = true;
      fetchTransactions();
    }, [fetchTransactions]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      // This effect now only runs on page and page size changes
      fetchTransactions(
        currentPage,
        pageSize,
        tableFilters,
        sortField,
        sortOrder
      );
    }, [currentPage, pageSize, fetchTransactions]);

    // This is the ONLY place filter state should be updated. Do NOT update tableFilters from any filter dropdown or child component.
    const handleTableChange = (pagination, filtersFromTable, sorter) => {
      // Clear transactions and set loading immediately
      setTransactions([]);
      setLoading(true);

      const mergedFilters = { ...filters, ...filtersFromTable };
      if (
          Array.isArray(filtersFromTable.transaction_date) &&
          filtersFromTable.transaction_date.length == 2
        ) {
          mergedFilters.dateRange = [
            dayjs(filtersFromTable.transaction_date[0]).startOf("day"),
            dayjs(filtersFromTable.transaction_date[1]).endOf("day"),
          ]
        } else {
          delete mergedFilters.dateRange;
        }
        
      const { current: newPage, pageSize: size } = pagination;

      const sortFieldMapping = {
        bank: "statement__bank__name",
        transaction_date: "transaction_date",
        amount: "amount",
        allocated: "allocated",
        description: "description",
        detail: "detail",
        type: "type",
        vendor: "partner__name",
      };

      const newSortOrder = sorter && sorter.order ? sorter.order : sortOrder;
      const newSortField =
        sorter && sorter.field
          ? sortFieldMapping[sorter.field] || sorter.field
          : sortField;

      const filtersChanged =
        JSON.stringify(mergedFilters) !== JSON.stringify(tableFilters);
      const sortChanged =
        newSortField !== sortField || newSortOrder !== sortOrder;
      const pageToFetch = filtersChanged || sortChanged ? 1 : newPage;

      // Update state
      setSortField(newSortField);
      setSortOrder(newSortOrder);
      setTableFilters(mergedFilters);
      setPageSize(size);
      setCurrentPage(pageToFetch);

      localStorage.setItem(
        PAGINATION_KEY,
        JSON.stringify({
          currentPage: pageToFetch,
          pageSize: size,
        })
      );

      // Persist filters and sort state to localStorage
      localStorage.setItem(
        FILTER_KEY,
        JSON.stringify({
          filters: mergedFilters,
          sortField: newSortField,
          sortOrder: newSortOrder,
        })
      );

      fetchTransactions(
        pageToFetch,
        size,
        mergedFilters,
        newSortField,
        newSortOrder
      );
    };

 

    const columns = [
      {
        title: "#",
        key: "index",
        render: (_, __, idx) => (currentPage - 1) * pageSize + idx + 1,
        width: 50,
      },
      {
        title: "Bank",
        dataIndex: ["bank", "name"],
        key: "bank",
        filters: [...new Set(filterValues.bank || [])].map((v) => ({
          text: v,
          value: v,
        })),
        filteredValue: tableFilters.bank || null,
        filterMultiple: true,
        filterSearch: true,
        width: 120,
      },
       {
          title: "Posting Date",
          dataIndex: "transaction_date",
          key: "transaction_date",
          // Use dayjs to format the date for display
          render: (d) => (d ? dayjs(d).format("MM/DD/YYYY") : ""),
          // Use dayjs for accurate date sorting
          sorter: (a, b) => dayjs(a.transaction_date).valueOf() - dayjs(b.transaction_date).valueOf(),
          width: 180,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
              <div style={{ padding: 8, minWidth: 320, display: "flex", flexDirection: "column" }}>
              <RangePicker
                // value should be an array of dayjs objects
                value={selectedKeys}
                onChange={(dates) => {
                  setSelectedKeys(dates ? dates : []);
                }}
                style={{ marginBottom: 8, width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={() => confirm()}
                  size="small"
                  style={{ width: 90 }}
                >
                  Filter
                </Button>
                <Button
                  onClick={() => {
                    clearFilters();
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Reset
                </Button>
              </Space>
            </div>
          ),
          // Convert stored date strings back to dayjs objects for the DatePicker
          filteredValue: tableFilters.dateRange
            ? [dayjs(tableFilters.dateRange[0]), dayjs(tableFilters.dateRange[1])]
            : null,
          onFilter: () => true,
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        filters: [...new Set(filterValues.description || [])].map((v) => ({
          text: v,
          value: v,
        })),
        filteredValue: tableFilters.description || null,
        filterMultiple: true,
        filterSearch: true,
        ellipsis: true,
        filterDropdown: (dropdownProps) => (
          <DescriptionFilterDropdown
            {...dropdownProps}
            filterValues={filterValues}
          />
        ),
        render: (text) => (
          <span
            style={{
              display: "inline-block",
              maxWidth: 400,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={text}
          >
            {text}
          </span>
        ),
      },
      {
        title: "Detail",
        dataIndex: "detail",
        key: "detail",
        filters: [...new Set(filterValues.detail || [])].map((v) => ({
          text: v,
          value: v,
        })),
        filteredValue: tableFilters.detail || null,
        filterMultiple: true,
        filterSearch: true,
        width: 120,
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (type) => type,
        filters: [...new Set(filterValues.type || [])].map((v) => ({
          text: v,
          value: v,
        })),
        filteredValue: tableFilters.type || null,
        filterMultiple: true,
        filterSearch: true,
        width: 150,
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        align: "right",
        sorter: true,
        render: (v) => `${parseFloat(v || 0).toFixed(2)}`,
        width: 120,
      },
      {
        title: "Allocated",
        key: "allocated",
        dataIndex: "allocated_amount", // ensure this matches the backend response
        width: 120,
        sorter: true,
        // render: (r) =>
        //   `${parseFloat(r.allocatedAmount || 0).toFixed(2)} (${parseFloat(
        //     r.percentAllocated || 0
        //   ).toFixed(1)}%)`,
      },
      {
        title: "Allocation Status",
        key: "allocationStatus",
        width: 160,
        render: (rec) => {
          const { type, label } = rec.allocationStatus;
          let style = {};
          if (type === "unallocated")
            style = { backgroundColor: "#f8d7da", color: "#721c24" };
          else if (type === "partially_allocated")
            style = { backgroundColor: "#fff3cd", color: "#856404" };
          else if (type === "overpay")
            style = { backgroundColor: "#cdfbff", color: "#000000" };
          else style = { backgroundColor: "#d4edda", color: "#155724" };
          return <Tag style={style}>{label}</Tag>;
        },
        filters: [
          { text: "Unallocated", value: "unallocated" },
          { text: "Partially Allocated", value: "partially_allocated" },
          { text: "Fully Allocated", value: "fully_allocated" },
          { text: "overpay", value: "overpay" },
        ],
        filteredValue: tableFilters.allocationStatus || null,
        filterMultiple: true,

      },
      {
        title: "Duplicate Status",
        dataIndex: "duplicate_status",
        key: "duplicate_status",
        render: (v) => (
          <Tag
            color={
              v === "DUPLICATE" ? "red" : v === "NEW_ENTRY" ? "green" : "orange"
            }
          >
            {v}
          </Tag>
        ),
        width: 140,
      },
      {
        title: "Vendor",
        key: "vendor",
        dataIndex: "vendor_name",
        render: (vendor_name) => vendor_name || "-",
        filters: [...new Set(filterValues.vendor || [])].map((v) => ({
          text: v,
          value: v,
        })),
        filteredValue: tableFilters.vendor || null,
        filterMultiple: true,
        filterSearch: true,
        width: 140,
      },
      {
        title: "Invoices",
        key: "invoices",
        width: 100,
        render: (rec) => {
          const inv = rec.allocations || [];
          if (!inv.length) return "No invoices";
          const unique = Array.from(
            new Set(inv.map((i) => i.invoice_number))
          ).length;
          const content = (
            <div style={{ width: 750 }}>
              <Table
                dataSource={inv}
                size="small"
                pagination={false}
                rowKey="invoice_number"
                columns={[
                  {
                    title: "Invoice #",
                    dataIndex: "invoice_number",
                    key: "invoice_number",
                    width: 100,
                  },
                  {
                    title: "Amt",
                    dataIndex: "amount",
                    key: "amount",
                    render: (a) => parseFloat(a).toFixed(2),
                    width: 90,
                  },
                  {
                    title: "Date",
                    dataIndex: "allocation_date",
                    key: "allocation_date",
                    render: (d) => new Date(d).toLocaleDateString(),
                    width: 100,
                  },
                ]}
              />
            </div>
          );
          return (
            <Popover
              content={content}
              title={`${unique} Invoice${unique > 1 ? "s" : ""}`}
              trigger="click"
            >
              <Button size="small" icon={<FileTextOutlined />}>
                {unique}
              </Button>
            </Popover>
          );
        },
      },
      {
        title: "Notes",
        dataIndex : "notes",
        key: "notes",
        width: 120,
        render: (text) => {
          if(!text) return "No notes";

          return(
            <Popover
              content={
                <div style={{ maxWidth: 400, whiteSpace: "pre-wrap" }}>
                  {text}
                </div>  
              }
              title = "Invoice Notes" 
              trigger="click"
              >
                <Button size="small" icon={<FileTextOutlined />}>
                  View Notes
                </Button>
              </Popover>
          )
        }
      },
      {
        title: "",
        key: "actions",
        fixed: "right",
        width: 120,
        render: (_, rec) => (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => onAffectPayment(rec.id)}
            >
              Pay
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => onAffectOverpayment(rec.id)}
            >
              O-Pay
            </Button>
          </Space>
        ),
      },
    ];

    console.log("transactions:", transactions);
    return (
      <div className="table-responsive p-x">
        {/* Header with Excel Export Button */}
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Button
              onClick={() => setExportModalVisible(true)}
              icon={<ImageWithBasePath src="assets/img/icons/xls.svg" alt="Excel" style={{ width: 16, height: 16 }} />}
              title="Export to Excel"
            >
              Export Excel
            </Button>
          </div>
        </div>

        <div className="fixed-table-container">
          <Table
            rowClassName={(rec) =>
              rec.allocationStatus.type === "overpay"
                ? "table-overpay"
                : rec.allocationStatus.type === "unallocated"
                ? "table-danger-outline"
                : rec.allocationStatus.type === "partially_allocated"
                ? "table-warning-outline"
                : "table-success-outline"
            }
            columns={columns}
            dataSource={transactions}
            rowKey="id"
            loading={loading}
            onChange={handleTableChange}
            size="small"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100", "200", "500"],
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} transactions`,
              position: ["bottomCenter"],
            }}
            scroll={{ x: "max-content", y: 500 }}
            sticky={{ offsetHeader: 0 }}
          />
        </div>

        <ExcelExportModal
          visible={exportModalVisible}
          onCancel={() => setExportModalVisible(false)}
          onExport={exportToExcel}
          exportType="Bank Statements"
          loading={exportLoading}
        />
      </div>
    );
  }
);

BankStatements.displayName = "BankStatements";

export default BankStatements;
